import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell from "../../components/AppShell";
import EmptyState from "../../components/EmptyState";
import StatusChip from "../../components/StatusChip";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Plus, Users2, ArrowRight, Repeat, Upload } from "lucide-react";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
// Release C-2 (P1) — Open in Microsoft 365 Mail for the Leads surface.
import OpenInMicrosoftMailButton from "../../design-system/microsoft365/OpenInMicrosoftMailButton";

const STATUSES = ["new","contacted","qualified","site_visit_scheduled","proposal_sent","won","lost","nurture"];
const SECTORS = ["residential","commercial","industrial","government","property_management"];

export default function Leads() {
  const [items, setItems] = useState([]);
  const [replyCounts, setReplyCounts] = useState({});
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", company: "", sector: "residential", project_type: "", notes: "", source: "Owner Entry" });
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filter = searchParams.get("filter") || "";
  // R1d.10 — When ?filter=new-this-hour, restrict to public_quote_form leads
  // created in the last 60 minutes (matches the Owner tile source of truth).
  const filteredItems = useMemo(() => {
    if (filter !== "new-this-hour") return items;
    const cutoff = Date.now() - 60 * 60 * 1000;
    return items.filter((l) => {
      if ((l.source || "").toLowerCase() !== "public_quote_form") return false;
      const created = l.created_at ? new Date(l.created_at).getTime() : 0;
      return created >= cutoff;
    });
  }, [items, filter]);
  const clearFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("filter");
    setSearchParams(next);
  };

  const load = async () => {
    try {
      const [leadsResp, countsResp] = await Promise.all([
        api.get("/leads"),
        api.get("/leads/unread-reply-counts").catch(() => ({ data: {} })),
      ]);
      setItems(leadsResp.data);
      setReplyCounts(countsResp.data || {});
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Authenticated, tenant-scoped create (tenant comes from the session server-side).
      const r = await api.post("/leads", form);
      toast.success(`Lead ${r.data?.lead_number || ""} created`.replace("  ", " "));
      setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", phone: "", company: "", sector: "residential", project_type: "", notes: "", source: "Owner Entry" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/leads/${id}`, { status });
      toast.success(`Status → ${status}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const convert = async (id) => {
    if (!window.confirm("Convert this lead to an Opportunity + Account + Contact?")) return;
    try {
      const r = await api.post(`/leads/${id}/convert`);
      toast.success("Converted → Opportunity created");
      navigate(`/crm/opportunities`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <AppShell
      title="CRM · Leads"
      subtitle={
        filter === "new-this-hour"
          ? `${filteredItems.length} inbound web lead(s) in the last 60 min · filtered`
          : `${items.length} lead(s)`
      }
      actions={
        <div className="flex gap-2">
          <Link to="/crm/bid-proposals/new" data-testid="leads-upload-proposal-link">
            <Button variant="outline" data-testid="leads-upload-proposal-btn">
              <Upload className="w-4 h-4 mr-1" /> Upload proposal
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="leads-add-btn">
              <Plus className="w-4 h-4 mr-1" /> New lead
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-erf-cream max-w-lg" data-testid="leads-add-dialog">
            <DialogHeader><DialogTitle className="font-display">Add lead</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase font-nav">First name</Label><Input required value={form.first_name} onChange={(e)=>setForm({...form, first_name: e.target.value})} className="mt-1" data-testid="lead-first-name" /></div>
                <div><Label className="text-xs uppercase font-nav">Last name</Label><Input required value={form.last_name} onChange={(e)=>setForm({...form, last_name: e.target.value})} className="mt-1" data-testid="lead-last-name" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs uppercase font-nav">Email</Label><Input type="email" value={form.email} onChange={(e)=>setForm({...form, email: e.target.value})} className="mt-1 font-mono" data-testid="lead-email" /></div>
                <div><Label className="text-xs uppercase font-nav">Phone</Label><Input value={form.phone} onChange={(e)=>setForm({...form, phone: e.target.value})} className="mt-1 font-mono" data-testid="lead-phone" /></div>
              </div>
              <div><Label className="text-xs uppercase font-nav">Company</Label><Input value={form.company} onChange={(e)=>setForm({...form, company: e.target.value})} className="mt-1" data-testid="lead-company" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-nav">Sector</Label>
                  <Select value={form.sector} onValueChange={(v)=>setForm({...form, sector: v})}>
                    <SelectTrigger className="mt-1" data-testid="lead-sector"><SelectValue /></SelectTrigger>
                    <SelectContent>{SECTORS.map(s=><SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs uppercase font-nav">Project type</Label><Input value={form.project_type} onChange={(e)=>setForm({...form, project_type: e.target.value})} className="mt-1" data-testid="lead-project-type" placeholder="Renovation, New build…" /></div>
              </div>
              <div><Label className="text-xs uppercase font-nav">Notes</Label><Textarea value={form.notes} onChange={(e)=>setForm({...form, notes: e.target.value})} className="mt-1" data-testid="lead-notes" rows={3} /></div>
              <Button type="submit" disabled={busy} className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="lead-submit">{busy ? "Saving…" : "Create lead"}</Button>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      }
    >
      {filter === "new-this-hour" && (
        <div className="mb-3 flex items-center justify-between rounded-md border border-erf-gold/30 bg-erf-gold/10 px-3 py-2" data-testid="leads-filter-banner">
          <div className="text-xs font-nav text-erf-dark">
            Showing <span className="font-semibold">inbound web contact-form leads</span> from the last 60 minutes.
          </div>
          <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={clearFilter} data-testid="leads-filter-clear">
            Clear filter
          </Button>
        </div>
      )}
      {/* 3c retrofit — universal intake at the module (list) level for CSV import + paste. */}
      <div className="mb-3 flex items-center justify-between rounded-md border border-erf-borderCream bg-white px-3 py-2" data-testid="leads-intake-block">
        <div className="text-xs font-nav text-erf-dark/70">
          Bulk actions — import a spreadsheet, paste a list, or attach a source document to leads intake.
        </div>
        <InformationIntake
          recordType="lead" recordId="module-leads"
          category="import_csv" confidentiality="internal"
          capabilities={["import_csv", "paste", "upload", "manual"]}
          onChanged={load}
        />
        <OpenInMicrosoftMailButton surface="lead_module" recordType="lead" recordId={null} />
      </div>
      {filteredItems.length === 0 ? (
        filter === "new-this-hour" ? (
          <EmptyState icon={Users2} title="No new inbound leads in the last hour" subtitle="This filter mirrors the Owner Command Center tile." />
        ) : (
          <EmptyState icon={Users2} title="No leads yet" subtitle="Add your first lead. Public /quote captures leads automatically too." />
        )
      ) : (
        <div className="bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table erf-stack-sm" data-testid="leads-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Sector</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredItems.map((l) => (
                <tr key={l.id} className="border-t border-erf-borderCream/60" data-testid={`lead-row-${l.id}`}>
                  <td className="px-3 py-2 font-nav font-semibold" data-label="Name">
                    <Link to={`/crm/leads/${l.id}`} className="hover:text-erf-goldHover inline-flex items-center gap-2 min-h-[44px] min-w-[44px]" data-testid={`lead-detail-link-${l.id}`}>
                      <span>{l.first_name} {l.last_name}</span>
                      {replyCounts[l.id] > 0 && (
                        <span
                          className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-erf-gold text-erf-dark text-[10px] font-bold font-mono leading-none"
                          aria-label={`${replyCounts[l.id]} unread ${replyCounts[l.id] === 1 ? "reply" : "replies"}`}
                          title={`${replyCounts[l.id]} unread reply${replyCounts[l.id] === 1 ? "" : "ies"}`}
                          data-testid={`lead-reply-count-${l.id}`}
                        >
                          {replyCounts[l.id]}
                        </span>
                      )}
                    </Link>
                    {l.company && <span className="block text-[11px] font-mono text-erf-dark/50">{l.company}</span>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px] break-all" data-label="Contact">
                    {l.email && <div>{l.email}</div>}
                    {l.phone && <div className="text-erf-dark/60">{l.phone}</div>}
                  </td>
                  <td className="px-3 py-2 mono text-[11px]" data-label="Sector">{l.sector}</td>
                  <td className="px-3 py-2 mono text-[11px] text-erf-dark/70" data-label="Source">{l.source}</td>
                  <td className="px-3 py-2" data-label="Status">
                    <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                      <SelectTrigger className="h-11 text-[11px] w-40" data-testid={`lead-status-${l.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                  <td className="px-3 py-2 text-right" data-label="Actions">
                    {/* FINAL-POLISH P2 — Row-level icon-only Open in Outlook (Owner/Admin). */}
                    <span className="inline-block align-middle mr-1">
                      <OpenInMicrosoftMailButton
                        surface="lead_row"
                        recordType="lead"
                        recordId={l.id}
                        iconOnly
                      />
                    </span>
                    {!l.converted_opportunity_id ? (
                      <Button size="sm" variant="ghost" className="h-11 text-erf-goldHover" onClick={() => convert(l.id)} data-testid={`lead-convert-${l.id}`}>
                        <Repeat className="w-4 h-4 mr-1" /> Convert
                      </Button>
                    ) : (
                      <Link to="/crm/opportunities" className="inline-flex" data-testid={`lead-opp-link-${l.id}`}>
                        <Button size="sm" variant="ghost" className="h-11">Opp <ArrowRight className="w-4 h-4 ml-1" /></Button>
                      </Link>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AppShell>
  );
}
